/**
 * The structural components — and the place the product's visual identity is
 * actually decided, because layout is what a user sees before they read
 * anything.
 *
 * # The register
 *
 * The one deliberate departure from how a dashboard usually looks. Exi Git does
 * not put things in cards. A card is a box drawn around content to say "these
 * belong together", and in a dense engineering interface it costs 16 pixels of
 * padding, a border, a shadow and a radius on every side to say something that
 * alignment already said. Twelve cards on a page is twelve boxes competing for
 * the attention the data wanted.
 *
 * A *register* says it with a hairline and a column grid instead: a titled
 * section, a rule under the title, rows separated by rules, columns that line
 * up down the page. It is the shape of a technical ledger or an instrument
 * readout, it costs almost no vertical space, and it is what makes Exi Git read
 * as an instrument rather than as a dashboard. DESIGN_LANGUAGE.md §1 asks for
 * "visual weight spent on content, not on chrome" and "whitespace is
 * structural, not generous"; this is what that looks like built.
 */

import type { ElementType, ReactNode } from 'react';

import { cx } from '~/lib/cx';

import styles from './Layout.module.css';

/* ====================================================================== *
 * Page scaffolding
 * ====================================================================== */

export interface PageHeaderProps {
  title: ReactNode;
  /** One line under the title. Context, never instructions. */
  description?: ReactNode;
  /** Breadcrumb or eyebrow, above the title. */
  above?: ReactNode;
  /** Primary and secondary actions, right-aligned on wide viewports. */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, above, actions, className }: PageHeaderProps) {
  return (
    <header className={cx(styles.pageHeader, className)}>
      {above ? <div className={styles.pageHeaderAbove}>{above}</div> : null}
      <div className={styles.pageHeaderRow}>
        <div className={styles.pageHeaderText}>
          <h1 className={styles.pageTitle}>{title}</h1>
          {description ? <p className={styles.pageDescription}>{description}</p> : null}
        </div>
        {actions ? <div className={styles.pageHeaderActions}>{actions}</div> : null}
      </div>
    </header>
  );
}

export interface SectionHeaderProps {
  title: ReactNode;
  // `| undefined` on every optional prop that is *passed through*, because
  // `exactOptionalPropertyTypes` (ADR-0003) distinguishes an absent key from a
  // present one holding undefined, and `description={maybeUndefined}` is the
  // second.
  description?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  /**
   * The heading level this renders.
   *
   * Explicit rather than inferred from nesting, because a document outline is
   * not something to guess at: a page with an `h1` and four `h3`s reads to a
   * screen-reader user as four missing sections.
   */
  as?: 'h2' | 'h3' | 'h4';
  id?: string | undefined;
  className?: string | undefined;
}

export function SectionHeader({
  title,
  description,
  actions,
  as: Heading = 'h2',
  id,
  className,
}: SectionHeaderProps) {
  return (
    <div className={cx(styles.sectionHeader, className)}>
      <div className={styles.sectionHeaderText}>
        <Heading className={styles.sectionTitle} {...(id ? { id } : {})}>
          {title}
        </Heading>
        {description ? <p className={styles.sectionDescription}>{description}</p> : null}
      </div>
      {actions ? <div className={styles.sectionHeaderActions}>{actions}</div> : null}
    </div>
  );
}

export interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  /**
   * Whether the rule means anything.
   *
   * A rule that separates two groups of content is a `<hr>` and is announced;
   * a rule that is there because the layout looked unfinished without one is
   * `decorative` and is hidden. Most are decorative, and saying so keeps a
   * screen reader from reading "separator" eleven times on one page.
   */
  decorative?: boolean;
  className?: string;
}

export function Divider({
  orientation = 'horizontal',
  decorative = true,
  className,
}: DividerProps) {
  return (
    <div
      className={cx(styles.divider, styles[orientation], className)}
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'separator', 'aria-orientation': orientation })}
    />
  );
}

export interface ToolbarProps {
  children: ReactNode;
  /** Pushes everything after it to the far edge. */
  className?: string;
  'aria-label'?: string;
}

export function Toolbar({ children, className, ...rest }: ToolbarProps) {
  return (
    <div className={cx(styles.toolbar, className)} role="toolbar" {...rest}>
      {children}
    </div>
  );
}

export function Spacer() {
  return <div className={styles.spacer} />;
}

/* ====================================================================== *
 * The register
 * ====================================================================== */

export interface RegisterProps {
  /** The section heading. Omit for a register that follows its own heading. */
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * An accessible name when there is no visible title.
   *
   * One of `title` or `aria-label` is always needed: a list of twelve rows with
   * no name is twelve rows of unexplained text to anybody who cannot see where
   * it sits on the page.
   */
  'aria-label'?: string;
  headingLevel?: 'h2' | 'h3' | 'h4';
  children: ReactNode;
  /** Rendered under the last row — "Showing 10 of 48", a link to more. */
  footer?: ReactNode;
  className?: string;
}

export function Register({
  title,
  description,
  actions,
  headingLevel = 'h2',
  children,
  footer,
  className,
  ...rest
}: RegisterProps) {
  return (
    <section className={cx(styles.register, className)} {...rest}>
      {title ? (
        <SectionHeader
          title={title}
          {...(description === undefined ? {} : { description })}
          {...(actions === undefined ? {} : { actions })}
          as={headingLevel}
          className={styles.registerHeader}
        />
      ) : null}
      <div className={styles.registerBody}>{children}</div>
      {footer ? <div className={styles.registerFooter}>{footer}</div> : null}
    </section>
  );
}

export interface RegisterRowProps {
  /**
   * The row's primary identity — a repository name, an event, a finding.
   * Rendered first and allowed to take the space the others do not.
   */
  children: ReactNode;
  /** Fixed-width content at the trailing edge: a status, a count, a time. */
  trailing?: ReactNode;
  /** A 16px glyph at the leading edge. */
  leading?: ReactNode;
  /**
   * Make the whole row activate.
   *
   * Renders as a `<button>` or, with `asChild`, whatever the caller supplies —
   * which is how a row that navigates stays an `<a>`. A `<div onClick>` is
   * never produced here.
   */
  as?: ElementType;
  onClick?: () => void;
  href?: string;
  className?: string;
}

export function RegisterRow({
  children,
  trailing,
  leading,
  as,
  onClick,
  href,
  className,
  ...rest
}: RegisterRowProps) {
  const interactive = Boolean(onClick ?? href ?? as);
  const Component: ElementType = as ?? (href ? 'a' : onClick ? 'button' : 'div');

  return (
    <Component
      className={cx(styles.row, interactive && styles.rowInteractive, className)}
      {...(Component === 'button' ? { type: 'button' } : {})}
      {...(href ? { href } : {})}
      {...(onClick ? { onClick } : {})}
      {...rest}
    >
      {leading ? <span className={styles.rowLeading}>{leading}</span> : null}
      <span className={styles.rowMain}>{children}</span>
      {trailing ? <span className={styles.rowTrailing}>{trailing}</span> : null}
    </Component>
  );
}

/** The first line of a row: the thing the row is about. */
export function RowTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx(styles.rowTitle, className)}>{children}</span>;
}

/** The second line: metadata, never information the first line needed. */
export function RowMeta({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx(styles.rowMeta, className)}>{children}</span>;
}

/* ====================================================================== *
 * Metric
 * ====================================================================== */

export interface MetricProps {
  label: ReactNode;
  value: ReactNode;
  /** A qualifier under the number — a delta, a denominator, a timestamp. */
  context?: ReactNode;
  className?: string;
}

/**
 * One reading.
 *
 * Deliberately not a card: a hairline on the leading edge, the number at
 * `--text-xl` and the label above it at `--text-sm`. A row of these reads as a
 * single instrument panel rather than as four boxes, which is the difference
 * between a cockpit and a marketing page.
 */
export function Metric({ label, value, context, className }: MetricProps) {
  return (
    <div className={cx(styles.metric, className)}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
      {context ? <span className={styles.metricContext}>{context}</span> : null}
    </div>
  );
}

export function MetricGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx(styles.metricGroup, className)}>{children}</div>;
}
